import { el } from "./dom";

export interface SelectorControlOptions<T> {
  readonly label: string;
  readonly value: T;
  readonly options: readonly T[];
  readonly format: (value: T) => string;
  readonly onChange: (next: T) => void;
}

/**
 * ◀ value ▶ row, used by SettingsScreen rows. Arrows cycle through `options`
 * (wrapping at both ends) and call `onChange` with the new value. The whole
 * row is keyboard-reachable and exposes the readable label for assistive tech.
 */
export function createSelectorControl<T>(opts: SelectorControlOptions<T>): HTMLElement {
  const index = opts.options.indexOf(opts.value);
  const safeIndex = index >= 0 ? index : 0;

  const step = (delta: 1 | -1): void => {
    const len = opts.options.length;
    if (len === 0) return;
    const next = opts.options[(safeIndex + delta + len) % len];
    if (next !== undefined) opts.onChange(next);
  };

  const stop = (handler: () => void) => (ev: Event): void => {
    ev.stopPropagation();
    handler();
  };

  const prev = el("button", {
    class: "selector__arrow",
    attrs: {
      type: "button",
      "aria-label": `${opts.label}: previous`,
      "data-selector-arrow": "prev",
    },
    on: { click: stop(() => step(-1)) },
  }, ["◀"]);

  const value = el("span", {
    class: "selector__value",
    text: opts.format(opts.value),
  });

  const next = el("button", {
    class: "selector__arrow",
    attrs: {
      type: "button",
      "aria-label": `${opts.label}: next`,
      "data-selector-arrow": "next",
    },
    on: { click: stop(() => step(1)) },
  }, ["▶"]);

  return el("div", {
    class: "selector",
    attrs: { role: "group", "aria-label": opts.label },
  }, [prev, value, next]);
}
